import React, { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Plus, Trash2, Loader2, Check, X, Edit2, Settings, Copy, Crown, Shield,
} from 'lucide-react';

type PermissionType = 
  | 'access_editor' | 'view_templates' | 'edit_templates' | 'delete_templates'
  | 'manage_own_templates' | 'use_kiosk' | 'view_data_panel' | 'use_json_editor'
  | 'batch_import' | 'print_labels' | 'export_pdf' | 'use_data_carousel'
  | 'change_label_size' | 'use_presets' | 'change_settings' | 'use_clipboard'
  | 'manage_elements' | 'use_alignment' | 'view_preview' | 'export_json'
  | 'import_templates' | 'use_grid' | 'use_layers_panel' | 'edit_properties'
  | 'view_changelog' | 'use_zoom' | 'manage_categories' | 'use_field_undo'
  | 'duplicate_templates' | 'submit_templates'
  | 'view_dashboard' | 'use_keyboard_shortcuts' | 'view_help'
  | 'share_templates' | 'lock_templates' | 'version_templates'
  | 'use_snap_to_grid' | 'use_rulers' | 'use_multi_select' | 'resize_elements'
  | 'configure_print_settings' | 'bulk_print' | 'reprint_labels'
  | 'use_csv_import' | 'edit_field_data' | 'use_barcode_scanner' | 'use_mobile_scan'
  | 'view_shared_templates' | 'comment_templates'
  | 'view_own_activity' | 'view_print_history';

const ALL_PERMISSIONS: { value: PermissionType; label: string; category: string }[] = [
  // Access
  { value: 'access_editor', label: 'Access Editor', category: 'Access' },
  { value: 'use_kiosk', label: 'Use Kiosk Mode', category: 'Access' },
  { value: 'change_settings', label: 'Change Settings', category: 'Access' },
  { value: 'view_changelog', label: 'View Changelog', category: 'Access' },
  { value: 'view_dashboard', label: 'View Dashboard', category: 'Access' },
  { value: 'use_keyboard_shortcuts', label: 'Keyboard Shortcuts', category: 'Access' },
  { value: 'view_help', label: 'View Help', category: 'Access' },
  // Templates
  { value: 'view_templates', label: 'View Templates', category: 'Templates' },
  { value: 'edit_templates', label: 'Edit Global Templates', category: 'Templates' },
  { value: 'delete_templates', label: 'Delete Templates', category: 'Templates' },
  { value: 'manage_own_templates', label: 'Manage Own', category: 'Templates' },
  { value: 'use_presets', label: 'Use Presets', category: 'Templates' },
  { value: 'import_templates', label: 'Import Templates', category: 'Templates' },
  { value: 'manage_categories', label: 'Manage Categories', category: 'Templates' },
  { value: 'duplicate_templates', label: 'Duplicate', category: 'Templates' },
  { value: 'submit_templates', label: 'Submit Templates', category: 'Templates' },
  { value: 'share_templates', label: 'Share Templates', category: 'Templates' },
  { value: 'lock_templates', label: 'Lock Templates', category: 'Templates' },
  { value: 'version_templates', label: 'Version History', category: 'Templates' },
  // Editor
  { value: 'manage_elements', label: 'Manage Elements', category: 'Editor' },
  { value: 'edit_properties', label: 'Edit Properties', category: 'Editor' },
  { value: 'use_clipboard', label: 'Clipboard', category: 'Editor' },
  { value: 'use_alignment', label: 'Alignment', category: 'Editor' },
  { value: 'change_label_size', label: 'Label Size', category: 'Editor' },
  { value: 'use_grid', label: 'Grid', category: 'Editor' },
  { value: 'use_layers_panel', label: 'Layers', category: 'Editor' },
  { value: 'use_zoom', label: 'Zoom', category: 'Editor' },
  { value: 'use_snap_to_grid', label: 'Snap to Grid', category: 'Editor' },
  { value: 'use_rulers', label: 'Rulers', category: 'Editor' },
  { value: 'use_multi_select', label: 'Multi-Select', category: 'Editor' },
  { value: 'resize_elements', label: 'Resize Elements', category: 'Editor' },
  // Printing
  { value: 'print_labels', label: 'Print Labels', category: 'Printing' },
  { value: 'export_pdf', label: 'Export PDF', category: 'Printing' },
  { value: 'configure_print_settings', label: 'Print Settings', category: 'Printing' },
  { value: 'bulk_print', label: 'Bulk Print', category: 'Printing' },
  { value: 'reprint_labels', label: 'Reprint Labels', category: 'Printing' },
  // Data
  { value: 'view_data_panel', label: 'View Data Panel', category: 'Data' },
  { value: 'batch_import', label: 'Batch Import', category: 'Data' },
  { value: 'use_data_carousel', label: 'Data Carousel', category: 'Data' },
  { value: 'use_json_editor', label: 'JSON Editor', category: 'Data' },
  { value: 'export_json', label: 'Export JSON', category: 'Data' },
  { value: 'use_field_undo', label: 'Field Undo', category: 'Data' },
  { value: 'use_csv_import', label: 'CSV Import', category: 'Data' },
  { value: 'edit_field_data', label: 'Edit Field Data', category: 'Data' },
  { value: 'use_barcode_scanner', label: 'Barcode Scanner', category: 'Data' },
  { value: 'use_mobile_scan', label: 'Mobile Scan', category: 'Data' },
  // Preview
  { value: 'view_preview', label: 'View Preview', category: 'Preview' },
  // Collaboration
  { value: 'view_shared_templates', label: 'View Shared', category: 'Collaboration' },
  { value: 'comment_templates', label: 'Comment Templates', category: 'Collaboration' },
  // Audit
  { value: 'view_own_activity', label: 'View Own Activity', category: 'Audit' },
  { value: 'view_print_history', label: 'View Print History', category: 'Audit' },
];

const CATEGORIES = ['Access', 'Templates', 'Editor', 'Printing', 'Data', 'Preview', 'Collaboration', 'Audit'];

interface Group {
  id: string;
  name: string;
  description: string | null;
  permissions: PermissionType[];
}

interface UserSummary {
  id: string;
  username: string;
  isAdmin: boolean;
  groups: { id: string; name: string }[];
}

interface GroupManagementProps {
  groups: Group[];
  users: UserSummary[];
  onRefreshGroups: () => void;
  onRefreshUsers: () => void;
}

export const GroupManagement: React.FC<GroupManagementProps> = ({
  groups, users, onRefreshGroups, onRefreshUsers,
}) => {
  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupDescription, setNewGroupDescription] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [editGroupOpen, setEditGroupOpen] = useState(false);
  const [editGroupName, setEditGroupName] = useState('');
  const [editGroupDescription, setEditGroupDescription] = useState('');

  useEffect(() => {
    if (!selectedGroup) return;

    const refreshedGroup = groups.find((group) => group.id === selectedGroup.id) ?? null;
    setSelectedGroup(refreshedGroup);
  }, [groups, selectedGroup?.id]);

  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) { toast.error('Enter a name'); return; }
    setIsCreating(true);
    const { error } = await supabase.from('groups').insert({ name: newGroupName.trim(), description: newGroupDescription.trim() || null });
    setIsCreating(false);
    if (error) { toast.error(error.message.includes('duplicate') ? 'Name exists' : 'Failed'); return; }
    toast.success('Group created');
    setNewGroupName(''); setNewGroupDescription('');
    onRefreshGroups();
  };

  const handleDeleteGroup = async (groupId: string, groupName: string) => {
    if (groupName === 'Users') { toast.error('Cannot delete default group'); return; }
    if (!confirm(`Delete "${groupName}"?`)) return;
    const { error } = await supabase.from('groups').delete().eq('id', groupId);
    if (error) { toast.error('Failed'); return; }
    toast.success('Deleted');
    if (selectedGroup?.id === groupId) setSelectedGroup(null);
    onRefreshGroups(); onRefreshUsers();
  };

  const handleUpdateGroup = async () => {
    if (!selectedGroup || !editGroupName.trim()) return;
    const { error } = await supabase.from('groups').update({ name: editGroupName.trim(), description: editGroupDescription.trim() || null }).eq('id', selectedGroup.id);
    if (error) { toast.error('Failed'); return; }
    toast.success('Updated');
    setEditGroupOpen(false);
    onRefreshGroups();
  };

  const handleTogglePermission = async (groupId: string, permission: PermissionType, enabled: boolean) => {
    setSelectedGroup((current) => current?.id === groupId
      ? {
          ...current,
          permissions: enabled
            ? [...new Set([...current.permissions, permission])]
            : current.permissions.filter((value) => value !== permission),
        }
      : current);

    if (enabled) {
      const { error } = await supabase.from('group_permissions').insert({ group_id: groupId, permission });
      if (error) {
        const isDuplicate = error.code === '23505' || error.message.toLowerCase().includes('duplicate');
        if (!isDuplicate) {
          toast.error('Failed');
        }
        onRefreshGroups();
        onRefreshUsers();
        return;
      }
    } else {
      const { error } = await supabase.from('group_permissions').delete().eq('group_id', groupId).eq('permission', permission);
      if (error) {
        toast.error('Failed');
        onRefreshGroups();
        onRefreshUsers();
        return;
      }
    }
    onRefreshGroups(); onRefreshUsers();
  };

  const handleToggleAll = async (groupId: string, enable: boolean) => {
    if (enable) {
      const group = groups.find(g => g.id === groupId);
      if (!group) return;
      const missing = ALL_PERMISSIONS.filter(p => !group.permissions.includes(p.value)).map(p => ({ group_id: groupId, permission: p.value }));
      if (missing.length > 0) {
        const { error } = await supabase.from('group_permissions').insert(missing);
        if (error) { toast.error('Failed'); return; }
      }
    } else {
      const { error } = await supabase.from('group_permissions').delete().eq('group_id', groupId);
      if (error) { toast.error('Failed'); return; }
    }
    toast.success(enable ? 'All granted' : 'All removed');
    onRefreshGroups(); onRefreshUsers();
  };

  const handleDuplicate = async (group: Group) => {
    const { data: ng, error } = await supabase.from('groups').insert({ name: `${group.name} (Copy)`, description: group.description }).select().single();
    if (error) { toast.error('Failed'); return; }
    if (group.permissions.length > 0) {
      await supabase.from('group_permissions').insert(group.permissions.map(p => ({ group_id: ng.id, permission: p })));
    }
    toast.success('Duplicated');
    onRefreshGroups();
  };

  const getUsersInGroup = (groupId: string) => users.filter(u => u.groups.some(g => g.id === groupId));

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-2xl font-bold">Groups</h2>
        <p className="text-sm text-muted-foreground">Manage permission groups and their access levels</p>
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        {/* List */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base">Groups</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 mb-4">
              <Input placeholder="Group name" value={newGroupName} onChange={(e) => setNewGroupName(e.target.value)} />
              <Textarea placeholder="Description (optional)" value={newGroupDescription} onChange={(e) => setNewGroupDescription(e.target.value)} rows={2} />
              <Button onClick={handleCreateGroup} disabled={isCreating || !newGroupName.trim()} className="w-full gap-2">
                {isCreating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Create Group
              </Button>
            </div>
            <Separator className="my-3" />
            <ScrollArea className="h-[400px]">
              <div className="space-y-2">
                {groups.map(group => (
                  <div
                    key={group.id}
                    className={`p-3 rounded-lg border cursor-pointer transition-colors ${selectedGroup?.id === group.id ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'}`}
                    onClick={() => setSelectedGroup(group)}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-sm">{group.name}</span>
                      <div className="flex items-center gap-1">
                        <Badge variant="secondary" className="text-[10px]">{group.permissions.length} perms</Badge>
                        <Badge variant="outline" className="text-[10px]">{getUsersInGroup(group.id).length} users</Badge>
                      </div>
                    </div>
                    {group.description && <p className="text-xs text-muted-foreground mt-1">{group.description}</p>}
                  </div>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Permissions */}
        <Card className="lg:col-span-3">
          <CardContent className="pt-6">
            {selectedGroup ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-lg">{selectedGroup.name}</h3>
                    {selectedGroup.description && <p className="text-xs text-muted-foreground">{selectedGroup.description}</p>}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => { setEditGroupName(selectedGroup.name); setEditGroupDescription(selectedGroup.description || ''); setEditGroupOpen(true); }}>
                      <Edit2 className="w-3.5 h-3.5 mr-1" />Edit
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleDuplicate(selectedGroup)}>
                      <Copy className="w-3.5 h-3.5 mr-1" />Duplicate
                    </Button>
                    <Button variant="ghost" size="sm" className="text-destructive" onClick={() => handleDeleteGroup(selectedGroup.id, selectedGroup.name)}>
                      <Trash2 className="w-3.5 h-3.5 mr-1" />Delete
                    </Button>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => handleToggleAll(selectedGroup.id, true)}>Grant All</Button>
                  <Button variant="outline" size="sm" onClick={() => handleToggleAll(selectedGroup.id, false)}>Revoke All</Button>
                  <Badge variant="secondary">{selectedGroup.permissions.length}/{ALL_PERMISSIONS.length}</Badge>
                </div>

                <ScrollArea className="h-[350px]">
                  {CATEGORIES.map(cat => {
                    const perms = ALL_PERMISSIONS.filter(p => p.category === cat);
                    return (
                      <div key={cat} className="mb-4">
                        <Label className="text-xs text-muted-foreground uppercase tracking-wide">{cat}</Label>
                        <div className="grid grid-cols-2 gap-1.5 mt-1.5">
                          {perms.map(perm => (
                            <div key={perm.value} className="flex items-center space-x-2 p-1.5 rounded hover:bg-muted/30">
                              <Checkbox
                                checked={selectedGroup.permissions.includes(perm.value)}
                                onCheckedChange={(c) => handleTogglePermission(selectedGroup.id, perm.value, !!c)}
                              />
                              <label className="text-sm cursor-pointer">{perm.label}</label>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </ScrollArea>

                <Separator />
                <div>
                  <Label className="text-xs text-muted-foreground uppercase tracking-wide">Members</Label>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {getUsersInGroup(selectedGroup.id).map(u => (
                      <Badge key={u.id} variant="secondary" className="text-xs">
                        {u.username}
                        {u.isAdmin && <Crown className="w-3 h-3 ml-1 text-primary" />}
                      </Badge>
                    ))}
                    {getUsersInGroup(selectedGroup.id).length === 0 && <span className="text-xs text-muted-foreground">No members</span>}
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-[400px] text-muted-foreground">
                <Settings className="w-12 h-12 opacity-30 mb-4" />
                <p className="text-sm">Select a group to manage permissions</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Edit Dialog */}
      <Dialog open={editGroupOpen} onOpenChange={setEditGroupOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit Group</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2"><Label>Name</Label><Input value={editGroupName} onChange={(e) => setEditGroupName(e.target.value)} /></div>
            <div className="space-y-2"><Label>Description</Label><Textarea value={editGroupDescription} onChange={(e) => setEditGroupDescription(e.target.value)} rows={3} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditGroupOpen(false)}>Cancel</Button>
            <Button onClick={handleUpdateGroup} disabled={!editGroupName.trim()}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
